import type { PickByValue } from 'utility-types'
import React, { type FC, type PropsWithChildren, useCallback, useEffect, useMemo, useRef } from 'react'

import type { ReadonlyRecord } from '../../../../utils'

import { type AppState, arePagesEquals, ConnectwareError, type Page, type PaginationOptions, type SortableColumn } from '../../../../domain'

import {
    type PageSubscriptionArgs,
    type ResourcesPaginationSizes,
    type SubscriptionFilterArgs,
    type SubscriptionUpdateParameters,
    toPageSizeOptions,
    type Usecases,
} from '../../../../application'

import { Table, type TableColumn, type TableCustomizedState, type TableLine, type TablePagination, type TableProps } from '../../common'
import { type AbsoluteRoutePathWithId, type RelativeRoutePathWithId, useRouteWithId } from '../../routing'
import { useAppState, useAppUsecase } from '../../State'
import { useRetryKey } from '../../utils'
import { ListBulkModalScope, useRecordPanelDetails, useRecordPanelHost } from '../Panel'
import { useColumnView } from './ColumnView'
import { useSelection, type UseSelectionProps } from './Selection'

/** The columns can only have their display changed */
type DisplayChangingCoercion<TableResource> = Partial<
    Readonly<{ [P in keyof TableResource]: Pick<TableColumn<TableResource[P], TableResource>, 'customCellRender' | 'label' | 'type'> }>
>

/** This type forces the sorting to now be a boolean, and only if the original resource also contains that column */
type SortableCoercion<DataResource> = Partial<Readonly<{ [P in SortableColumn<DataResource>]: Readonly<{ sort?: true }> }>>

export type Columns<DataResource, TableResource> = DisplayChangingCoercion<TableResource> & SortableCoercion<DataResource>

type ManagementUsecase<DataResource> = Readonly<{
    /** Used to subscribe to changes */
    subscribe(args: PageSubscriptionArgs): VoidFunction
    /** Used to update the pagination parameters */
    updateParameters(update: SubscriptionUpdateParameters<DataResource>): void
    getPaginationSizes(): ResourcesPaginationSizes
    getResourcesSearchThreshold(): number
}>

type ResourceManagementUsecaseNames = keyof PickByValue<Usecases, ManagementUsecase<any>>

type ManagementUsecaseResource<U extends ManagementUsecase<any>> = U extends ManagementUsecase<infer R> ? R : never
type ManagementUsecasesResource<U extends ResourceManagementUsecaseNames> = ManagementUsecaseResource<Usecases[U]>

type Props<
    SubscriptionUsecaseName extends ResourceManagementUsecaseNames,
    DataResource extends ManagementUsecasesResource<SubscriptionUsecaseName>,
    TableResource extends TableLine,
    ManagementUsecases extends keyof Usecases,
> =
    & Readonly<{
        /** The usecase the table will use to fetch data */
        subscriptionUsecase: SubscriptionUsecaseName

        /** Filter parameters for the resources to be fetched */
        filter?: SubscriptionFilterArgs

        /** How data should be provided for the table to be rendered */
        data: (s: AppState) => Page<DataResource>

        /**
         * Once errors/loading states have been filtered out
         * This function will be called to assist to creation of the contents of the table itself
         */
        dataTableMapper: (resource: readonly DataResource[]) => readonly TableResource[]

        /** The columns of the table */
        columns: Columns<DataResource, TableResource>

        /** If the list should be short */
        short?: boolean
    }>
    & Pick<TableProps<TableResource>, 'onRowClick' | 'onRowOpenInNewTab' | 'translations'>
    & UseSelectionProps<TableResource, ManagementUsecases>

type PageDataState<DataResource> = NonNullable<Page<DataResource>>['data']
type LoadedPageData<DataResource> = Exclude<PageDataState<DataResource>, ConnectwareError | null>

const isPageDataLoaded = <DataResource,>(data: PageDataState<DataResource>): data is LoadedPageData<DataResource> => data !== null && !ConnectwareError.is(data)

/** A loaded page, with the page index and size it was loaded for */
type LoadedPage<DataResource> = Readonly<{ data: LoadedPageData<DataResource>, pagination: PaginationOptions }>

type ShownPageData<DataResource> = Readonly<{
    /** The current page, or else the last loaded one, as long as it belongs to the same subscription; one object per loaded page */
    page: LoadedPage<DataResource> | null
    /** If the current page itself has loaded */
    loaded: boolean
}>

const useLastLoadedPageData = <DataResource,>(page: Page<DataResource>, subscriptionKey: string): ShownPageData<DataResource> => {
    const committedSubscriptionKey = useRef(subscriptionKey)
    const last = useRef<Readonly<{ subscriptionKey: string, page: LoadedPage<DataResource> }> | null>(null)

    /** On the render where the subscription changes, the page still holds the previous subscription's data */
    const pageData = page?.data ?? null
    const currentData = committedSubscriptionKey.current === subscriptionKey && isPageDataLoaded(pageData) ? pageData : null
    const pageIndex = page?.pagination.page ?? null
    const pageSize = page?.pagination.pageSize ?? null

    const current = useMemo(
        () => (currentData !== null && pageIndex !== null && pageSize !== null ? { data: currentData, pagination: { page: pageIndex, pageSize } } : null),
        [currentData, pageIndex, pageSize]
    )

    useEffect(() => {
        committedSubscriptionKey.current = subscriptionKey

        if (current !== null) {
            last.current = { subscriptionKey, page: current }
        }
    }, [current, subscriptionKey])

    if (current !== null) {
        return { page: current, loaded: true }
    }

    return { page: last.current?.subscriptionKey === subscriptionKey ? last.current.page : null, loaded: false }
}

const mapTableCustomizedStateToSubscriptionUpdateParameters = <TableResource, DataResource>(
    s: TableCustomizedState<TableResource>
): SubscriptionUpdateParameters<DataResource> => {
    if ('search' in s) {
        /**
         * Search is nullable on SubscriptionUpdateParameters
         * @see {SubscriptionUpdateParameters['search']}
         */
        return { search: s.search || null }
    }

    if ('sortOrder' in s) {
        return {
            /**
             * This coercion is fine, as the type SortableCoercion above is only allowing sorting for column in data
             * @see {SortableCoercion}
             */
            sort: { column: s.sortOrder.name as SortableColumn<DataResource> & typeof s.sortOrder.name, asc: s.sortOrder.direction === 'asc' },
        }
    }

    /** These can be passed as is */
    return s
}

type ResourcesTableState<TableResource extends TableLine> = Readonly<{
    /** What the Table itself is given, children aside */
    tableProps: TableProps<TableResource> & Readonly<{ 'data-testid': string }>
    /** The rows shown, the current page's or else the last loaded one's */
    rows: readonly TableResource[]
    /** If the current page itself has loaded */
    loaded: boolean
    /** If the current page failed to load */
    failed: boolean
    /** The page of the `rows`, which while not `loaded` can be behind the page requested; one object per loaded page */
    pagination: (PaginationOptions & Readonly<{ totalCount: number }>) | null
    /** Requests another page, keeping the page size, sorting and search */
    turnPage: (page: number) => void
}>

/**
 * The state behind a table that
 *
 * - While mounted, subscribes to the given usecase (if the same is subscribable)
 * - Handles state changes (loading, error, success) of the given resources in order to properly render the contents
 */
const useResourcesTable = <
    DataResource extends ManagementUsecasesResource<ResourceManagementUsecaseNames>,
    TableResource extends TableLine,
    ManagementUsecases extends keyof Usecases,
>({
    subscriptionUsecase: subscriptionUsecaseName,
    filter = {},
    data: dataSelector,
    hasManagementCapabilitiesSelector,
    dataTableMapper,
    translations,
    selection,
    short = false,
    ...props
}: Props<ResourceManagementUsecaseNames, DataResource, TableResource, ManagementUsecases>): ResourcesTableState<TableResource> => {
    const subscriptionUsecase = useAppUsecase(subscriptionUsecaseName) as ManagementUsecase<DataResource>

    const resourcesPage = useAppState<Page<DataResource>>(dataSelector, arePagesEquals)
    const selectionConfig = useSelection({ hasManagementCapabilitiesSelector, selection })
    const { columnView, onColumnViewChange } = useColumnView(subscriptionUsecaseName, short, filter)
    const shownData = useLastLoadedPageData(resourcesPage, JSON.stringify([filter.connection, filter.server, filter.service, short]))

    /** Retrying drops the current subscription and subscribes again, which starts the page over */
    const [subscriptionAttempt, retrySubscription] = useRetryKey()

    /** Subscribe to changes of resources */
    useEffect(
        () => subscriptionUsecase.subscribe({ ...filter, short }),
        [subscriptionUsecase, filter.connection, filter.server, filter.service, short, subscriptionAttempt]
    )

    /** Load the possible page sizes to be used */
    const [pageSizeOptions, searchThreshold] = useMemo(
        () => [toPageSizeOptions(subscriptionUsecase.getPaginationSizes()), subscriptionUsecase.getResourcesSearchThreshold()],
        []
    )

    const onCustomized = useCallback(
        (s: TableCustomizedState<TableResource>) => subscriptionUsecase.updateParameters(mapTableCustomizedStateToSubscriptionUpdateParameters(s)),
        [subscriptionUsecase]
    )

    const turnPage = useCallback((page: number) => subscriptionUsecase.updateParameters({ page }), [subscriptionUsecase])

    const shownPage = shownData.page
    const totalCount = shownPage?.data.totalCount ?? null
    const pagination = useMemo(() => shownPage && { ...shownPage.pagination, totalCount: shownPage.data.totalCount }, [shownPage])

    const tablePagination: TablePagination | undefined = resourcesPage
        ? { ...resourcesPage.pagination, pageSizeOptions, ...(totalCount !== null && { totalCount }), showTotalCount: true }
        : undefined

    const error = resourcesPage !== null && ConnectwareError.is(resourcesPage.data) ? { error: resourcesPage.data, onRetry: retrySubscription } : undefined

    const rows = shownPage === null ? [] : dataTableMapper(shownPage.data.current)

    return {
        tableProps: {
            'data-testid': subscriptionUsecaseName,
            error,
            selection: selectionConfig,
            search: resourcesPage === null || resourcesPage.search === null || resourcesPage.search,
            searchThreshold,
            data: rows,
            loading: !shownData.loaded,
            pagination: tablePagination,
            sortOrder: resourcesPage ? { name: resourcesPage.sort.column, direction: resourcesPage.sort.asc ? 'asc' : 'desc' } : undefined,
            translations,
            onCustomized,
            sortData: false,
            columnView,
            onColumnViewChange,
            ...props,
        },
        rows,
        loaded: shownData.loaded,
        failed: error !== undefined,
        pagination,
        turnPage,
    }
}

/**
 * Renders the resources of `useResourcesTable` in a table with a standardized look and feel
 *
 * Children will be rendered within the Table component
 */
export const ResourcesTable = <
    SubscriptionUsecaseName extends ResourceManagementUsecaseNames,
    DataResource extends ManagementUsecasesResource<SubscriptionUsecaseName>,
    TableResource extends TableLine = DataResource & TableLine,
    ManagementUsecases extends keyof Usecases = never,
>({ children, ...props }: PropsWithChildren<Props<ResourceManagementUsecaseNames, DataResource, TableResource, ManagementUsecases>>): ReturnType<FC> => {
    const { tableProps } = useResourcesTable(props)
    return <Table {...tableProps}>{children}</Table>
}

type RedirectingProps<
    SubscriptionUsecaseName extends ResourceManagementUsecaseNames,
    DataResource extends ManagementUsecasesResource<SubscriptionUsecaseName>,
    TableResource extends TableLine,
    ManagementUsecases extends keyof Usecases,
> =
    & Omit<Props<ResourceManagementUsecaseNames, DataResource, TableResource, ManagementUsecases>, 'onRowClick' | 'onRowOpenInNewTab'>
    & Readonly<{ redirectOnRowclick: AbsoluteRoutePathWithId | RelativeRoutePathWithId }>

/**
 * The base resource needs an id for redirecting else where
 */
type RedirectingResource = TableLine & ReadonlyRecord<'id', string>

type RouteWithId = NonNullable<ReturnType<typeof useRouteWithId>>

const openInNewTab = (route: RouteWithId, id: string): void => {
    window.open(route.createHref(id), '_blank', 'noopener')
}

const PanelHostingResourcesTable = <
    DataResource extends ManagementUsecasesResource<ResourceManagementUsecaseNames> & RedirectingResource,
    TableResource extends RedirectingResource,
    ManagementUsecases extends keyof Usecases,
>({
    route,
    details,
    children,
    ...props
}: PropsWithChildren<
    & Omit<RedirectingProps<ResourceManagementUsecaseNames, DataResource, TableResource, ManagementUsecases>, 'redirectOnRowclick'>
    & Readonly<{ route: RouteWithId, details: FC }>
>): ReturnType<FC> => {
    const { tableProps, ...shown } = useResourcesTable(props)
    const host = useRecordPanelHost({ route, details, ...shown })

    return (
        <>
            <Table {...tableProps} {...host.tableProps} onRowOpenInNewTab={({ id }) => openInNewTab(route, id)}>
                <ListBulkModalScope openId={host.modalsOwnerId} onOpenRecordSelected={host.onOpenRecordSelected}>
                    {children}
                </ListBulkModalScope>
            </Table>
            {host.panel}
        </>
    )
}

/**
 * A row opens its record in a record panel, if there are details for its route and the table isn't in a panel itself, or else navigates to it
 */
export const RedirectingResourcesTable = <
    SubscriptionUsecaseName extends ResourceManagementUsecaseNames,
    DataResource extends ManagementUsecasesResource<SubscriptionUsecaseName> & RedirectingResource =
        & ManagementUsecasesResource<SubscriptionUsecaseName>
        & RedirectingResource,
    TableResource extends RedirectingResource = DataResource,
    ManagementUsecases extends keyof Usecases = never,
>({
    redirectOnRowclick,
    ...props
}: PropsWithChildren<RedirectingProps<ResourceManagementUsecaseNames, DataResource, TableResource, ManagementUsecases>>): ReturnType<FC> => {
    const route = useRouteWithId(redirectOnRowclick)
    const details = useRecordPanelDetails(route?.path ?? null)

    if (!route) {
        return <ResourcesTable {...props} />
    }

    if (details) {
        return <PanelHostingResourcesTable {...props} route={route} details={details} />
    }

    return <ResourcesTable {...props} onRowClick={({ id }) => route.redirect(id)} onRowOpenInNewTab={({ id }) => openInNewTab(route, id)} />
}
